import BaseStepHandler from './BaseStepHandler';
import { Image, Group } from 'fabric';
import SignatureModal from '../../ui/SignatureModal';

/**
 * Handler for signature step type
 */
export default class SignatureStepHandler extends BaseStepHandler {
    /**
     * Create new SignatureStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.signatureModal = null;
        this.groupId = `${this.stepId}_signature_group`;
        this.signatureId = `${this.stepId}_signature`;
        this.signatureGroup = null;
    }
    
    /**
     * Initialize signature elements
     */
    initialize() {
        super.initialize();

        // Get background element ID from definition
        const backgroundId = this.definition.elements?.[0];
        if (!backgroundId) return this;
        
        // Get background element from canvas
        let backgroundElement = this.canvas.getElement(backgroundId);
        if (!backgroundElement) return this;
        
        // Store reference to background element
        this.elements.set(backgroundId, backgroundElement);

        // Check if we already have a signature group
        this.signatureGroup = this.canvas.getElement(this.groupId);

        // If no group exists, create one
        if (!this.signatureGroup) {
            // Create group with background element
            this.signatureGroup = new Group([backgroundElement], {
                name: this.groupId,
                left: backgroundElement.left,
                top: backgroundElement.top,
                selectable: this.isActive,
                hasControls: this.isActive,
                originX: 'center',
                originY: 'center',
                visible: false, // Start hidden until data determines visibility
                subTargetCheck: false
            });
            
            // Remove background from canvas and add new group
            this.canvas.canvas.remove(backgroundElement);
            this.canvas.canvas.add(this.signatureGroup);
            
            // Add to reference maps
            this.canvas.elementRefs.set(this.groupId, this.signatureGroup);
            this.elements.set(this.groupId, this.signatureGroup);
        }
        
        return this;
    }

    /**
     * Process template when no data exists
     */
    processTemplate() {
        // Get step data
        const stepData = this.getStepData();
        
        // If we have data, update with it (will handle visibility)
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
        } else {
            // Otherwise, hide the group as signature is optional by default
            if (this.definition.required !== true) {
                this.hide();
            } else {
                this.show();
            }
        }
        
        return this;
    }
    
    /**
     * Activate signature element
     */
    activate() {
        super.activate();
        
        // If signature group exists and is enabled, make it interactive
        const stepData = this.getStepData();
        if (stepData && stepData.enabled && this.signatureGroup) {
            this.signatureGroup.set({
                selectable: true,
                hasControls: true,
                lockMovementX: false,
                lockMovementY: false,
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                borderScaleFactor:10,
                padding: 10
            });
            
            this.canvas.canvas.setActiveObject(this.signatureGroup);
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Deactivate signature element
     */
    deactivate() {
        super.deactivate();
        
        // Make signature group non-interactive
        if (this.signatureGroup) {
            this.signatureGroup.set({
                selectable: false,
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true
            });
            
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Update signature element with step data
     *
     * @param {object} stepData - Step data
     */
    update(stepData) {
        // Check if the signature is enabled
        if (!stepData || stepData.enabled !== true) {
            this.hide();
            return this;
        }

        this.show();

        // IMPORTANT: Restore group position if saved (for edit mode)
        if (stepData.groupPosition && this.signatureGroup) {
            this.signatureGroup.set({
                left: stepData.groupPosition.left,
                top: stepData.groupPosition.top,
                scaleX: stepData.groupPosition.scaleX || 1,
                scaleY: stepData.groupPosition.scaleY || 1,
                angle: stepData.groupPosition.angle || 0
            });
            this.signatureGroup.setCoords();
            console.log('[SignatureStepHandler] Restored group position:', stepData.groupPosition);
        }

        const sigImg = this.getElement(this.signatureId);

        if (sigImg && stepData.previewUrl &&
            this.lastStepData && this.lastStepData.previewUrl === stepData.previewUrl) {

            const scale = stepData.signatureScale || 4;
            this.signatureGroup.remove(sigImg);
            sigImg.set({
                name: this.signatureId,
                originX: 'center',
                originY: 'center',
                left: this.signatureGroup.left,
                top: this.signatureGroup.top,
                selectable: false,
                hasControls: false,
                scaleX: scale,
                scaleY: scale
            });

            // Add to group
            this.signatureGroup.add(sigImg);
            this.signatureGroup.setCoords();
            this.canvas.canvas.requestRenderAll();
        } else if (stepData.previewUrl) {
            this.createOrUpdateSignature(stepData);
        }

        this.lastStepData = {...stepData};

        return this;
    }
    
    /**
     * Create or update signature on canvas
     * 
     * @param {object} stepData - Step data
     */
    createOrUpdateSignature(stepData) {
        if (!this.signatureGroup) return;
        
        // Check if we already have a signature image
        let sigImg = this.getElement(this.signatureId);
        
        // Get background element to calculate constraints
        const backgroundId = this.definition.elements?.[0];
        const backgroundElement = this.getElement(backgroundId);
        
        if (!backgroundElement) return;
        
        // Create or update signature image
        Image.fromURL(stepData.previewUrl).then((img) => {
            // Remove old signature if it exists
            if (sigImg) {
                this.signatureGroup.remove(sigImg);
                this.elements.delete(this.signatureId);
                this.canvas.elementRefs.delete(this.signatureId);
            }
            
            // Get user-specified scale factor
            const userScale = stepData.signatureScale || 4;
            
            // Set up new signature image with proper positioning
            img.set({
                name: this.signatureId,
                originX: 'center',
                originY: 'center',
                left: this.signatureGroup.left,
                top: this.signatureGroup.top,
                selectable: false,
                hasControls: false,
                scaleX: userScale,
                scaleY: userScale
            });
            
            // Add to group
            this.signatureGroup.add(img);
            
            // Add to reference maps
            this.canvas.elementRefs.set(this.signatureId, img);
            this.elements.set(this.signatureId, img);
            
            // Ensure group is centered
            this.signatureGroup.setCoords();
            
            // Render
            this.canvas.canvas.requestRenderAll();
        });
    }
    
    /**
     * Handle side change
     * @param {boolean} isCurrentSide - Whether this handler's side is now active
     */
    handleSideChange(isCurrentSide) {
        if (isCurrentSide) {
            // This handler's side is now active
            this.processTemplate();
        } else {
            // Always hide when switching to other side
            this.hide();
        }
        
        return this;
    }
    
    /**
     * Show elements managed by this handler
     */
    show() {
        if (this.signatureGroup) {
            this.signatureGroup.set('visible', true);
            this.isVisible = true;
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Hide elements managed by this handler
     */
    hide() {
        if (this.signatureGroup) {
            this.signatureGroup.set('visible', false);
            this.isVisible = false;
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData();
        const price = this.definition.price || 0;
        
        return `
            <div class="stc-signature-field">
                <label class="stc-switch">
                    <input type="checkbox" 
                            class="stc-signature-toggle"
                            ${stepData?.enabled ? 'checked' : ''}
                            data-price="${price}">
                    <span class="stc-slider"></span>
                </label>
                <span class="stc-toggle-label">
                    Enable ${this.definition.title}
                    ${price ? `<span class="stc-price">(+$${price})</span>` : ''}
                </span>
    
                <div class="stc-signature-preview ${stepData?.enabled ? '' : 'stc-signature-disabled'}">
                    ${stepData?.previewUrl ? 
                        `<img src="${stepData.previewUrl}" alt="Signature">` : 
                        '<div class="stc-signature-placeholder">No signature yet</div>'
                    }
                </div>
                
                ${stepData?.previewUrl ? `
                    <div class="stc-signature-scale-control">
                        <label for="signature-scale-${this.stepId}">Signature Size</label>
                        <input type="range" 
                                id="signature-scale-${this.stepId}" 
                                class="stc-signature-scale-slider" 
                                min="1" 
                                max="15.0" 
                                step="0.5" 
                                value="${stepData.signatureScale || 4}">
                    </div>
                ` : ''}
                
                <button type="button" class="stc-signature-configure-btn" ${stepData?.enabled ? '' : 'disabled'}>
                    ${stepData?.previewUrl ? 'Edit Signature' : 'Create Signature'}
                </button>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const toggleSwitch = this.panelElement.querySelector('.stc-signature-toggle');
        const configureButton = this.panelElement.querySelector('.stc-signature-configure-btn');
        const scaleSlider = this.panelElement.querySelector('.stc-signature-scale-slider');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        const previewContainer = this.panelElement.querySelector('.stc-signature-preview');
        
        // Bind toggle switch
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Update preview container
                if (previewContainer) {
                    previewContainer.classList.toggle('stc-signature-disabled', !stepData.enabled);
                }
                
                // Update configure button
                if (configureButton) {
                    configureButton.disabled = !stepData.enabled;
                }
                
                // Update clear button
                if (clearButton) {
                    clearButton.disabled = !stepData.enabled;
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind configure button
        if (configureButton) {
            this.addEventListener(configureButton, 'click', () => {
                this.openSignatureModal();
            });
        }
        
        // Bind scale slider
        if (scaleSlider) {
            this.addEventListener(scaleSlider, 'input', () => {
                const stepData = this.getStepData() || {};
                stepData.signatureScale = parseFloat(scaleSlider.value);
                this.setStepData(stepData);
            });
        }
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }
    
    /**
     * Initialize and open signature modal
     */
    openSignatureModal() {
        if (!this.signatureModal) {
            this.signatureModal = new SignatureModal();
            
            // Add save handler
            this.signatureModal.on('save', ({ detail }) => {
                const stepData = this.getStepData() || { enabled: true };
                stepData.signature = detail;
                
                // Generate preview URL
                if (detail.type === 'text') {
                    // Create a canvas to render text signature
                    const canvas = document.createElement('canvas');
                    canvas.width = 600;
                    canvas.height = 300;
                    const ctx = canvas.getContext('2d');
                    
                    // Set up font and style
                    ctx.font = `${detail.size * 2}px ${detail.font}`;
                    ctx.fillStyle = '#000000';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    
                    // Draw text
                    ctx.fillText(detail.text || 'Your Signature', 300, 150);
                    
                    // Get data URL
                    stepData.previewUrl = canvas.toDataURL('image/png');
                } else {
                    // For drawn signature, use the data URL directly
                    stepData.previewUrl = detail.dataUrl;
                }
                
                // Set default scale if not already set
                if (!stepData.signatureScale) stepData.signatureScale = 4;
                
                this.setStepData(stepData);
                this.updatePanel();
            });
        }
        
        this.signatureModal.show();
    }

    /**
     * Step-specific reset logic
     */
    onReset() {
        // Remove signature image if it exists
        const sigImg = this.getElement(this.signatureId);
        if (sigImg && this.signatureGroup) {
            this.signatureGroup.remove(sigImg);
            this.elements.delete(this.signatureId);
            this.canvas.elementRefs.delete(this.signatureId);
            this.canvas.canvas.requestRenderAll();
        }
    }

    /**
     * Get export data for signature step
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const exportData = super.getStepExportData();
        const stepData = this.getStepData();

        // Capture current group position if it exists
        let groupPosition = null;
        if (this.signatureGroup) {
            groupPosition = {
                left: this.signatureGroup.left,
                top: this.signatureGroup.top,
                scaleX: this.signatureGroup.scaleX || 1,
                scaleY: this.signatureGroup.scaleY || 1,
                angle: this.signatureGroup.angle || 0
            };
        }

        // Add signature-specific data
        exportData.signatureContent = {
            type: stepData.signature?.type || 'none',
            scale: stepData.signatureScale || 4,

            // IMPORTANT: Save group position for restoration during edit
            groupPosition: groupPosition,

            // For text signatures
            textSignature: stepData.signature?.type === 'text' ? {
                text: stepData.signature.text || '',
                font: stepData.signature.font || 'Arial',
                size: stepData.signature.size || 40
            } : null,

            // Image data for the signature
            imageSource: stepData.previewUrl ? {
                type: stepData.signature?.type === 'text' ? 'generatedSignature' : 'drawnSignature',
                url: stepData.previewUrl,
                needsProcessing: true,
                metadata: {
                    fileName: `signature_${this.stepId}.png`,
                    fileType: 'image/png',
                    generateOnServer: stepData.signature?.type === 'text' // Flag for server-side generation for text signatures
                }
            } : null
        };

        return exportData;
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clean up signature modal if it exists
        if (this.signatureModal) {
            this.signatureModal.off('save');
        }
        
        // Call parent dispose
        super.dispose();
    }

    isComplete() {
        const stepData = this.getStepData() || {};
        return stepData.enabled && stepData.previewUrl;
    }

    /**
     * Check if signature step has content that needs to be saved
     *
     * @param {Object} stepData - Current step data
     * @returns {boolean} Whether step has saveable content
     */
    hasSaveableContent(stepData) {
        // Only save if:
        // 1. Signature is enabled
        // 2. We have a signature preview URL
        // 3. The previewUrl is a data URL (needs processing) OR it's NOT an existing signature
        // If it's an existing signature with HTTP URL, no need to re-upload
        if (!stepData || stepData.enabled !== true || !stepData.previewUrl || !stepData.signature) {
            return false;
        }

        // If it's an existing signature that hasn't changed, no need to save
        if (stepData.isExistingSignature && !stepData.previewUrl.startsWith('data:')) {
            return false;
        }

        // New signature (data URL) needs to be saved
        return stepData.previewUrl.startsWith('data:');
    }

    /**
     * Create save operations for signature content
     * 
     * @param {Object} stepData - Current step data
     * @returns {Promise<Array>} Array of save operation objects
     */
    async createSaveOperations(stepData) {
        const operations = [];
        
        if (!this.hasSaveableContent(stepData)) {
            return operations;
        }
        
        // Generate a unique reference ID for this signature
        const signatureReferenceId = `signature_${this.side}_${this.stepId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        
        // Create the save operation for the signature image
        operations.push({
            type: 'image_upload',
            referenceId: signatureReferenceId,
            dataUrl: stepData.previewUrl,
            imageType: 'signature',
            metadata: {
                stepId: this.stepId,
                side: this.side,
                signatureType: stepData.signature.type, // 'text' or 'drawn'
                fileName: `signature_${this.stepId}_${this.side}.png`,
                fileType: 'image/png',
                isSignature: true,
                
                // Include signature-specific metadata
                signatureData: {
                    type: stepData.signature.type,
                    scale: stepData.signatureScale || 4,
                    
                    // For text signatures, include the text details for potential server-side regeneration
                    ...(stepData.signature.type === 'text' && {
                        textSignature: {
                            text: stepData.signature.text || '',
                            font: stepData.signature.font || 'Arial',
                            size: stepData.signature.size || 40
                        }
                    })
                }
            }
        });
        
        // Store the reference ID in step data for later use
        const updatedStepData = { ...stepData };
        updatedStepData.signatureReferenceId = signatureReferenceId;
        this.setStepData(updatedStepData, false);
        
        return operations;
    }

    /**
     * Get priority for signature save operations
     * Signatures are important but not as critical as main profile images
     * 
     * @returns {number} Priority level (0-10)
     */
    getSaveOperationsPriority() {
        return 7; // High priority but below main images
    }

    /**
     * Validate that signature save operations completed successfully
     * 
     * @param {Array} completedOperations - Operations that were processed
     * @returns {boolean} Whether this step's requirements were met
     */
    validateSaveOperations(completedOperations) {
        const stepData = this.getStepData();
        
        // If no signature is enabled, validation passes
        if (!this.hasSaveableContent(stepData)) {
            return true;
        }
        
        // Look for our signature operation in the completed operations
        const signatureOperation = completedOperations.find(op => 
            op.sourceStepId === this.stepId && 
            op.sourceSide === this.side &&
            op.type === 'image_upload' &&
            op.referenceId === stepData.signatureReferenceId
        );
        
        if (!signatureOperation) {
            console.error(`Signature save operation not found for ${this.stepId}`);
            return false;
        }
        
        // Check if the operation has a result with a URL
        if (!signatureOperation.result || !signatureOperation.result.url) {
            console.error(`Signature save operation failed for ${this.stepId}:`, signatureOperation.error);
            return false;
        }
        
        console.log(`Signature save validation passed for ${this.stepId}:`, signatureOperation.result.url);
        return true;
    }

    /**
     * Get image reference for export (updated to work with new save system)
     *
     * @returns {object|null} Image reference data or null if no signature
     */
    getImageReferenceForExport() {
        const stepData = this.getStepData();

        // If no signature data at all, return null
        if (!stepData || !stepData.previewUrl || !stepData.signature) {
            return null;
        }

        // Determine if this signature needs processing (new = data: URL)
        const needsProcessing = stepData.previewUrl && stepData.previewUrl.startsWith('data:');

        // If this is an existing signature that hasn't changed, preserve original reference
        if (!needsProcessing && stepData.isExistingSignature) {
            return {
                referenceId: stepData.signatureReferenceId || `signature_${this.side}_${this.stepId}_${Date.now()}`,
                type: 'signature',
                side: this.side,
                stepId: this.stepId,
                // Preserve the original upload data
                id: stepData.attachmentId,
                url: stepData.existingUrl,
                processed: true,
                metadata: {
                    fileName: `signature_${this.stepId}_${this.side}.png`,
                    fileType: 'image/png',
                    isSignature: true,
                    signatureType: stepData.signature?.type || 'text'
                }
            };
        }

        // New signature needs processing - only if it's a data URL
        if (!needsProcessing) {
            return null; // HTTP URL but not marked as existing - skip
        }

        // Create the reference object for new signatures
        const reference = {
            referenceId: stepData.signatureReferenceId || `signature_${this.stepId}_${Date.now()}`,
            stepId: this.stepId,
            side: this.side,
            type: 'signature',
            needsProcessing: true,
            metadata: {
                fileName: `signature_${this.stepId}_${this.side}.png`,
                fileType: 'image/png',
                isSignature: true,
                signatureType: stepData.signature?.type || 'text'
            }
        };

        // Include the data URL for processing
        reference.dataUrl = stepData.previewUrl;

        return reference;
    }

}